import { useState, useEffect, useCallback } from 'react';
import * as Sentry from '@sentry/react';
import { supabase } from '@/lib/supabaseClient';
import { toast } from 'sonner';

export type Temporada = {
  id_horario_temporada: string;
  id_empresa: string;
  nombre: string;
  fecha_desde: string;
  fecha_hasta: string;
  cierre: boolean;
  activo: boolean;
};

export type TemporadaForm = {
  nombre: string;
  fecha_desde: string;
  fecha_hasta: string;
};

// Fecha local en formato ISO. Con toISOString() se usaria UTC y a partir de
// las 22:00 en España el dia saltaria al siguiente.
export function fechaHoyLocal(d = new Date()) {
  return [
    d.getFullYear(),
    String(d.getMonth() + 1).padStart(2, '0'),
    String(d.getDate()).padStart(2, '0'),
  ].join('-');
}

export function formatearFechaCorta(iso: string) {
  const [a, m, d] = iso.split('-');
  return `${d}/${m}/${a}`;
}

// Misma regla que temporada_vigente en base de datos: si hay solapes,
// gana la temporada de rango mas corto.
export function buscarTemporadaVigente(
  temporadas: Temporada[],
  fecha = fechaHoyLocal()
): Temporada | null {
  const dias = (t: Temporada) =>
    new Date(t.fecha_hasta).getTime() - new Date(t.fecha_desde).getTime();

  return (
    temporadas
      .filter(
        (t) => t.activo && t.fecha_desde <= fecha && fecha <= t.fecha_hasta
      )
      .sort((a, b) => dias(a) - dias(b))[0] ?? null
  );
}

export function useTemporadas(idEmpresa: string) {
  const [temporadas, setTemporadas] = useState<Temporada[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const fetchTemporadas = useCallback(async () => {
    if (!idEmpresa) return [];

    setLoading(true);

    const { data, error } = await supabase
      .from('horario_temporada')
      .select('id_horario_temporada, id_empresa, nombre, fecha_desde, fecha_hasta, cierre, activo')
      .eq('id_empresa', idEmpresa)
      .eq('activo', true)
      .order('fecha_desde');

    setLoading(false);

    if (error) {
      Sentry.captureException(error, { extra: { idEmpresa } });
      toast.error('Error al cargar las temporadas');
      return [];
    }

    setTemporadas(data as Temporada[]);
    return data as Temporada[];
  }, [idEmpresa]);

  useEffect(() => {
    fetchTemporadas();
  }, [fetchTemporadas]);

  const validar = (form: TemporadaForm) => {
    if (!form.nombre.trim()) return 'Ponle un nombre a la temporada';
    if (!form.fecha_desde || !form.fecha_hasta) return 'Faltan las fechas';
    if (form.fecha_hasta < form.fecha_desde)
      return 'La fecha de fin debe ser posterior a la de inicio';
    return null;
  };

  const crearTemporada = async (form: TemporadaForm) => {
    const err = validar(form);
    if (err) {
      toast.error(err);
      return { ok: false as const };
    }

    setSaving(true);

    try {
      const { data, error } = await supabase
        .from('horario_temporada')
        .insert({
          id_empresa: idEmpresa,
          nombre: form.nombre.trim(),
          fecha_desde: form.fecha_desde,
          fecha_hasta: form.fecha_hasta,
          usuario_creacion: localStorage.getItem('id_usuario') || null,
        })
        .select('id_horario_temporada')
        .single();

      if (error) throw error;

      await fetchTemporadas();
      toast.success('Temporada creada');
      return { ok: true as const, idTemporada: data.id_horario_temporada as string };
    } catch (error) {
      Sentry.captureException(error, { extra: { idEmpresa } });
      toast.error('No se pudo crear la temporada');
      return { ok: false as const };
    } finally {
      setSaving(false);
    }
  };

  const editarTemporada = async (idTemporada: string, form: TemporadaForm) => {
    const err = validar(form);
    if (err) {
      toast.error(err);
      return { ok: false as const };
    }

    setSaving(true);

    try {
      const { error } = await supabase
        .from('horario_temporada')
        .update({
          nombre: form.nombre.trim(),
          fecha_desde: form.fecha_desde,
          fecha_hasta: form.fecha_hasta,
          usuario_edicion: localStorage.getItem('id_usuario') || null,
        })
        .eq('id_horario_temporada', idTemporada);

      if (error) throw error;

      await fetchTemporadas();
      toast.success('Temporada actualizada');
      return { ok: true as const };
    } catch (error) {
      Sentry.captureException(error, { extra: { idTemporada } });
      toast.error('No se pudo actualizar la temporada');
      return { ok: false as const };
    } finally {
      setSaving(false);
    }
  };

  // El borrado arrastra las franjas de empresa y trabajador por el cascade.
  const borrarTemporada = async (idTemporada: string) => {
    setSaving(true);

    try {
      const { error } = await supabase
        .from('horario_temporada')
        .delete()
        .eq('id_horario_temporada', idTemporada);

      if (error) throw error;

      await fetchTemporadas();
      toast.success('Temporada eliminada');
      return { ok: true as const };
    } catch (error) {
      Sentry.captureException(error, { extra: { idTemporada } });
      toast.error('No se pudo eliminar la temporada');
      return { ok: false as const };
    } finally {
      setSaving(false);
    }
  };

  const vigenteHoy = buscarTemporadaVigente(temporadas);

  return {
    temporadas,
    vigenteHoy,
    loading,
    saving,
    refrescar: fetchTemporadas,
    crearTemporada,
    editarTemporada,
    borrarTemporada,
  };
}